/**
 * TestcaseManagerPage Component
 * 
 * 테스트케이스 관리 페이지 - 목업 버전
 * 향후 테스트케이스 직접 생성/관리 기능 구현 예정
 * 반응형: 모바일 / 태블릿 / 데스크탑
 */

import { useState, useMemo } from 'react';
import { useLanguage } from '../i18n';
import { useAuth } from '../auth';

// 목업 테스트케이스 타입
interface MockTestcase {
  id: string;
  title: string;
  description: string;
  category: 'core' | 'edge' | 'regression' | 'smoke';
  priority: 'high' | 'medium' | 'low';
  status: 'draft' | 'active' | 'passed' | 'failed' | 'skipped';
  expectedResult: string;
  createdAt: string;
  updatedAt: string;
}

// 목업 데이터
const MOCK_TESTCASES: MockTestcase[] = [
  {
    id: 'TC-001',
    title: '로그인 정상 동작 확인',
    description: '유효한 이메일/비밀번호로 로그인 시 홈 화면으로 이동',
    category: 'core',
    priority: 'high',
    status: 'passed',
    expectedResult: '로그인 성공 후 홈 화면 표시',
    createdAt: '2025-12-20T10:00:00Z',
    updatedAt: '2025-12-24T15:30:00Z',
  },
  {
    id: 'TC-002',
    title: '잘못된 비밀번호 입력 시 에러 표시',
    description: '잘못된 비밀번호 입력 시 적절한 에러 메시지 표시',
    category: 'edge',
    priority: 'high',
    status: 'passed',
    expectedResult: '비밀번호가 일치하지 않습니다 메시지 표시',
    createdAt: '2025-12-20T10:30:00Z',
    updatedAt: '2025-12-24T15:30:00Z',
  },
  {
    id: 'TC-003',
    title: '네트워크 오류 시 재시도 버튼 표시',
    description: 'API 호출 실패 시 재시도 버튼과 에러 메시지 표시',
    category: 'edge',
    priority: 'medium',
    status: 'failed',
    expectedResult: '네트워크 오류 메시지와 재시도 버튼 표시',
    createdAt: '2025-12-21T09:00:00Z',
    updatedAt: '2025-12-25T10:00:00Z',
  },
  {
    id: 'TC-004',
    title: '대시보드 로딩 성능',
    description: '대시보드 페이지 로딩 시간 3초 이내',
    category: 'smoke',
    priority: 'medium',
    status: 'active',
    expectedResult: '3초 이내 대시보드 렌더링 완료',
    createdAt: '2025-12-22T14:00:00Z',
    updatedAt: '2025-12-22T14:00:00Z',
  },
  {
    id: 'TC-005',
    title: '이전 버전 호환성 검증',
    description: '기존 사용자 데이터가 새 버전에서 정상 표시',
    category: 'regression',
    priority: 'low',
    status: 'draft',
    expectedResult: '기존 데이터 정상 마이그레이션 및 표시',
    createdAt: '2025-12-23T11:00:00Z',
    updatedAt: '2025-12-23T11:00:00Z',
  },
];

interface TestcaseManagerPageProps {
  onBack: () => void;
  onUseInEvaluation?: (testcases: MockTestcase[]) => void;
}

export default function TestcaseManagerPage({ onBack, onUseInEvaluation }: TestcaseManagerPageProps) {
  const { t, lang } = useLanguage();
  const { isAuthenticated } = useAuth();
  
  // 상태
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState<'all' | 'active' | 'archived'>('all');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'name'>('newest');
  const [selectedTestcases, setSelectedTestcases] = useState<Set<string>>(new Set());
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // 필터링 & 정렬된 테스트케이스
  const filteredTestcases = useMemo(() => {
    let result = [...MOCK_TESTCASES];
    
    // 검색
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(tc => 
        tc.title.toLowerCase().includes(query) ||
        tc.description.toLowerCase().includes(query) ||
        tc.id.toLowerCase().includes(query)
      );
    }
    
    // 필터
    if (selectedFilter === 'active') {
      result = result.filter(tc => tc.status !== 'draft');
    } else if (selectedFilter === 'archived') {
      result = result.filter(tc => tc.status === 'draft');
    }
    
    // 정렬
    result.sort((a, b) => {
      if (sortBy === 'newest') {
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      } else if (sortBy === 'oldest') {
        return new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
      } else {
        return a.title.localeCompare(b.title);
      }
    });
    
    return result;
  }, [searchQuery, selectedFilter, sortBy]);

  // 카테고리 라벨
  const getCategoryLabel = (category: MockTestcase['category']) => {
    const labels: Record<string, { ko: string; en: string; color: string }> = {
      core: { ko: '핵심', en: 'Core', color: 'bg-status-go text-white' },
      edge: { ko: '엣지', en: 'Edge', color: 'bg-status-warning text-black' },
      regression: { ko: '회귀', en: 'Regression', color: 'bg-purple-500 text-white' },
      smoke: { ko: '스모크', en: 'Smoke', color: 'bg-blue-500 text-white' },
    };
    return labels[category] || labels.core;
  };

  // 상태 라벨
  const getStatusLabel = (status: MockTestcase['status']) => {
    const labels: Record<string, { ko: string; en: string; color: string }> = {
      draft: { ko: '초안', en: 'Draft', color: 'bg-terminal-muted text-white' },
      active: { ko: '활성', en: 'Active', color: 'bg-blue-500 text-white' },
      passed: { ko: '통과', en: 'Passed', color: 'bg-status-go text-white' },
      failed: { ko: '실패', en: 'Failed', color: 'bg-status-danger text-white' },
      skipped: { ko: '건너뜀', en: 'Skipped', color: 'bg-terminal-dim text-white' },
    };
    return labels[status] || labels.draft;
  };

  // 우선순위 라벨
  const getPriorityLabel = (priority: MockTestcase['priority']) => {
    const labels: Record<string, { ko: string; en: string; icon: string }> = {
      high: { ko: '높음', en: 'High', icon: '🔴' },
      medium: { ko: '보통', en: 'Medium', icon: '🟡' },
      low: { ko: '낮음', en: 'Low', icon: '🟢' },
    };
    return labels[priority] || labels.medium;
  };

  // 선택 토글
  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedTestcases);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedTestcases(newSelected);
  };

  // 전체 선택/해제
  const toggleSelectAll = () => {
    if (selectedTestcases.size === filteredTestcases.length) {
      setSelectedTestcases(new Set());
    } else {
      setSelectedTestcases(new Set(filteredTestcases.map(tc => tc.id)));
    }
  };

  // 비로그인 시
  if (!isAuthenticated) {
    return (
      <div className="neo-card-static bg-[#1e2a3a] p-8 text-center" data-testid="testcase-manager-login-required">
        <h2 className="text-xl font-bold text-terminal-text mb-4">
          {t.auth.loginRequired}
        </h2>
        <button
          onClick={onBack}
          className="neo-btn bg-white text-black px-6 py-3 mx-auto"
        >
          {t.dashboard.backToHome}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6" data-testid="testcase-manager-page">
      {/* 목업 안내 배너 */}
      <div className="neo-card-static bg-gradient-to-r from-[var(--neo-orange)] to-[var(--neo-yellow)] p-4" data-testid="testcase-mockup-banner">
        <div className="flex items-center gap-3">
          <span className="text-2xl">🚧</span>
          <div>
            <h3 className="text-sm font-bold text-black">
              {t.testcaseManager?.mockupTitle || '🚧 개발 예정 기능'}
            </h3>
            <p className="text-xs text-black/70 whitespace-pre-line">
              {t.testcaseManager?.mockupDesc || '테스트케이스 관리 기능은 곧 출시됩니다!\n아래는 기능 미리보기입니다.'}
            </p>
          </div>
        </div>
      </div>

      {/* 헤더 */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-testid="testcase-header">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="neo-btn bg-white dark:bg-[var(--neo-blue)] text-black text-sm px-3 py-1.5"
            data-testid="testcase-back-btn"
          >
            {t.dashboard.backToHome}
          </button>
          <h1 className="text-lg md:text-xl font-black text-black dark:text-white uppercase">
            {t.testcaseManager?.title || '테스트케이스 관리'}
          </h1>
        </div>
        
        <div className="flex gap-2">
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="neo-btn bg-[var(--neo-mint)] text-black px-4 py-2 text-sm flex items-center gap-2"
            data-testid="testcase-create-btn"
          >
            <span>➕</span>
            <span className="hidden sm:inline">{t.testcaseManager?.createNew || '새 테스트케이스'}</span>
          </button>
          <button
            className="neo-btn bg-white dark:bg-[var(--neo-blue)] text-black px-4 py-2 text-sm flex items-center gap-2"
            data-testid="testcase-import-btn"
          >
            <span>📥</span>
            <span className="hidden sm:inline">{t.testcaseManager?.importFile || '가져오기'}</span>
          </button>
        </div>
      </div>

      {/* 검색 & 필터 바 */}
      <div className="neo-card-static bg-white dark:bg-[#1e2a3a] p-4" data-testid="testcase-filters">
        <div className="flex flex-col md:flex-row gap-3">
          {/* 검색 */}
          <div className="flex-1 relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t.testcaseManager?.searchPlaceholder || '테스트케이스 검색...'}
              className="w-full pl-10 pr-4 py-2.5 bg-terminal-input border-2 border-black dark:border-terminal-border rounded-none text-sm text-terminal-text placeholder:text-terminal-muted focus:outline-none focus:border-[var(--neo-mint)]"
              data-testid="testcase-search-input"
            />
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-terminal-muted">🔍</span>
          </div>
          
          {/* 필터 */}
          <div className="flex gap-2">
            <select
              value={selectedFilter}
              onChange={(e) => setSelectedFilter(e.target.value as 'all' | 'active' | 'archived')}
              className="px-3 py-2.5 bg-terminal-input border-2 border-black dark:border-terminal-border rounded-none text-sm text-terminal-text focus:outline-none focus:border-[var(--neo-mint)]"
              data-testid="testcase-filter-select"
            >
              <option value="all">{t.testcaseManager?.filterAll || '전체'}</option>
              <option value="active">{t.testcaseManager?.filterActive || '활성'}</option>
              <option value="archived">{t.testcaseManager?.filterArchived || '보관됨'}</option>
            </select>
            
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'newest' | 'oldest' | 'name')}
              className="px-3 py-2.5 bg-terminal-input border-2 border-black dark:border-terminal-border rounded-none text-sm text-terminal-text focus:outline-none focus:border-[var(--neo-mint)]"
              data-testid="testcase-sort-select"
            >
              <option value="newest">{t.testcaseManager?.sortNewest || '최신순'}</option>
              <option value="oldest">{t.testcaseManager?.sortOldest || '오래된순'}</option>
              <option value="name">{t.testcaseManager?.sortName || '이름순'}</option>
            </select>
          </div>
        </div>
      </div>

      {/* 선택 액션 바 */}
      {selectedTestcases.size > 0 && (
        <div className="neo-card-static bg-[var(--neo-blue)] dark:bg-[#2d3a4f] p-3 flex items-center justify-between" data-testid="testcase-selection-bar">
          <span className="text-sm font-bold text-black dark:text-white">
            {selectedTestcases.size} {lang === 'ko' ? '개 선택됨' : ' selected'}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => {
                const selected = filteredTestcases.filter(tc => selectedTestcases.has(tc.id));
                onUseInEvaluation?.(selected);
              }}
              className="neo-btn bg-[var(--neo-mint)] text-black px-3 py-1.5 text-xs"
              data-testid="testcase-use-in-eval-btn"
            >
              {t.testcaseManager?.useInEvaluation || '평가에 사용'}
            </button>
            <button className="neo-btn bg-status-danger text-white px-3 py-1.5 text-xs" data-testid="testcase-delete-selected-btn">
              {t.testcaseManager?.delete || '삭제'}
            </button>
          </div>
        </div>
      )}

      {/* 테스트케이스 목록 */}
      <div className="space-y-3" data-testid="testcase-list">
        {/* 헤더 행 (데스크탑) */}
        <div className="hidden lg:flex items-center gap-4 px-4 py-2 text-xs font-bold text-terminal-muted uppercase">
          <div className="w-6">
            <input
              type="checkbox"
              checked={selectedTestcases.size === filteredTestcases.length && filteredTestcases.length > 0}
              onChange={toggleSelectAll}
              className="w-4 h-4 rounded border-2 border-terminal-border"
              data-testid="testcase-select-all"
            />
          </div>
          <div className="w-20">ID</div>
          <div className="flex-1">{t.testcaseManager?.titleLabel || '제목'}</div>
          <div className="w-20">{t.testcaseManager?.category || '카테고리'}</div>
          <div className="w-16">{t.testcaseManager?.priority || '우선순위'}</div>
          <div className="w-16">{t.testcaseManager?.status || '상태'}</div>
          <div className="w-24">{t.testcaseManager?.lastUpdated || '수정일'}</div>
          <div className="w-20"></div>
        </div>

        {filteredTestcases.length === 0 ? (
          <div className="neo-card-static bg-white dark:bg-[#1e2a3a] p-8 text-center" data-testid="testcase-empty">
            <span className="text-4xl mb-4 block">📋</span>
            <h3 className="text-lg font-bold text-terminal-text mb-2">
              {t.testcaseManager?.noTestcases || '테스트케이스가 없습니다'}
            </h3>
            <p className="text-sm text-terminal-muted">
              {t.testcaseManager?.noTestcasesDesc || '새 테스트케이스를 만들거나 파일을 가져와보세요'}
            </p>
          </div>
        ) : (
          filteredTestcases.map((tc) => {
            const category = getCategoryLabel(tc.category);
            const status = getStatusLabel(tc.status);
            const priority = getPriorityLabel(tc.priority);
            
            return (
              <div
                key={tc.id}
                className={`neo-card-static bg-white dark:bg-[#1e2a3a] p-4 transition-all ${
                  selectedTestcases.has(tc.id) ? 'ring-2 ring-[var(--neo-mint)]' : ''
                }`}
                data-testid={`testcase-item-${tc.id}`}
              >
                {/* 모바일/태블릿 레이아웃 */}
                <div className="lg:hidden">
                  <div className="flex items-start gap-3 mb-3">
                    <input
                      type="checkbox"
                      checked={selectedTestcases.has(tc.id)}
                      onChange={() => toggleSelect(tc.id)}
                      className="w-4 h-4 mt-1 rounded border-2 border-terminal-border"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="text-xs font-mono text-terminal-muted">{tc.id}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${category.color}`}>
                          {lang === 'ko' ? category.ko : category.en}
                        </span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${status.color}`}>
                          {lang === 'ko' ? status.ko : status.en}
                        </span>
                      </div>
                      <h4 className="text-sm font-bold text-terminal-text mb-1">{tc.title}</h4>
                      <p className="text-xs text-terminal-muted line-clamp-2">{tc.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-terminal-muted">
                      {priority.icon} {lang === 'ko' ? priority.ko : priority.en}
                    </span>
                    <div className="flex gap-2">
                      <button className="text-xs text-terminal-muted hover:text-terminal-text" data-testid={`testcase-edit-${tc.id}`}>
                        ✏️
                      </button>
                      <button className="text-xs text-terminal-muted hover:text-status-danger" data-testid={`testcase-delete-${tc.id}`}>
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>

                {/* 데스크탑 레이아웃 */}
                <div className="hidden lg:flex items-center gap-4">
                  <div className="w-6">
                    <input
                      type="checkbox"
                      checked={selectedTestcases.has(tc.id)}
                      onChange={() => toggleSelect(tc.id)}
                      className="w-4 h-4 rounded border-2 border-terminal-border"
                    />
                  </div>
                  <div className="w-20 text-xs font-mono text-terminal-muted">{tc.id}</div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-bold text-terminal-text truncate">{tc.title}</h4>
                    <p className="text-xs text-terminal-muted truncate">{tc.description}</p>
                  </div>
                  <div className="w-20">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${category.color}`}>
                      {lang === 'ko' ? category.ko : category.en}
                    </span>
                  </div>
                  <div className="w-16 text-xs">
                    {priority.icon}
                  </div>
                  <div className="w-16">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${status.color}`}>
                      {lang === 'ko' ? status.ko : status.en}
                    </span>
                  </div>
                  <div className="w-24 text-xs text-terminal-muted">
                    {new Date(tc.updatedAt).toLocaleDateString(lang === 'ko' ? 'ko-KR' : 'en-US', {
                      month: 'short',
                      day: 'numeric',
                    })}
                  </div>
                  <div className="w-20 flex gap-2 justify-end">
                    <button 
                      className="neo-btn bg-white text-black px-2 py-1 text-[10px]"
                      data-testid={`testcase-edit-desktop-${tc.id}`}
                    >
                      {t.testcaseManager?.edit || '편집'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* 통계 푸터 */}
      <div className="flex flex-wrap gap-4 text-xs text-terminal-muted justify-center" data-testid="testcase-stats">
        <span>📊 {lang === 'ko' ? '총' : 'Total'}: {MOCK_TESTCASES.length}</span>
        <span>✅ {lang === 'ko' ? '통과' : 'Passed'}: {MOCK_TESTCASES.filter(tc => tc.status === 'passed').length}</span>
        <span>❌ {lang === 'ko' ? '실패' : 'Failed'}: {MOCK_TESTCASES.filter(tc => tc.status === 'failed').length}</span>
        <span>📝 {lang === 'ko' ? '초안' : 'Draft'}: {MOCK_TESTCASES.filter(tc => tc.status === 'draft').length}</span>
      </div>

      {/* 생성 모달 (목업) */}
      {isCreateModalOpen && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setIsCreateModalOpen(false)}
          data-testid="testcase-create-modal"
        >
          <div 
            className="neo-card-static bg-white dark:bg-[#1a1a2e] max-w-lg w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-black text-black dark:text-white">
                {t.testcaseManager?.createTitle || '새 테스트케이스 만들기'}
              </h3>
              <button
                onClick={() => setIsCreateModalOpen(false)}
                className="text-terminal-muted hover:text-terminal-text"
                data-testid="testcase-create-modal-close"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-terminal-muted mb-1.5">
                  {t.testcaseManager?.titleLabel || '제목'} *
                </label>
                <input
                  type="text"
                  placeholder={t.testcaseManager?.titlePlaceholder || '테스트케이스 제목을 입력하세요'}
                  className="w-full px-3 py-2.5 bg-terminal-input border-2 border-black dark:border-terminal-border text-sm text-terminal-text placeholder:text-terminal-muted focus:outline-none focus:border-[var(--neo-mint)]"
                  data-testid="testcase-create-title-input"
                />
              </div>
              
              <div>
                <label className="block text-xs font-bold text-terminal-muted mb-1.5">
                  {t.testcaseManager?.description || '설명'}
                </label>
                <textarea
                  placeholder={t.testcaseManager?.descriptionPlaceholder || '테스트 시나리오를 설명하세요...'}
                  rows={3}
                  className="w-full px-3 py-2.5 bg-terminal-input border-2 border-black dark:border-terminal-border text-sm text-terminal-text placeholder:text-terminal-muted focus:outline-none focus:border-[var(--neo-mint)] resize-none"
                  data-testid="testcase-create-desc-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-terminal-muted mb-1.5">
                    {t.testcaseManager?.category || '카테고리'}
                  </label>
                  <select
                    className="w-full px-3 py-2.5 bg-terminal-input border-2 border-black dark:border-terminal-border text-sm text-terminal-text focus:outline-none focus:border-[var(--neo-mint)]"
                    data-testid="testcase-create-category-select"
                  >
                    <option value="core">{t.testcaseManager?.categoryCore || '핵심'}</option>
                    <option value="edge">{t.testcaseManager?.categoryEdge || '엣지'}</option>
                    <option value="regression">{t.testcaseManager?.categoryRegression || '회귀'}</option>
                    <option value="smoke">{t.testcaseManager?.categorySmoke || '스모크'}</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs font-bold text-terminal-muted mb-1.5">
                    {t.testcaseManager?.priority || '우선순위'}
                  </label>
                  <select
                    className="w-full px-3 py-2.5 bg-terminal-input border-2 border-black dark:border-terminal-border text-sm text-terminal-text focus:outline-none focus:border-[var(--neo-mint)]"
                    data-testid="testcase-create-priority-select"
                  >
                    <option value="high">{t.suggestedTestcases?.priorityHigh || '높음'}</option>
                    <option value="medium">{t.suggestedTestcases?.priorityMedium || '중간'}</option>
                    <option value="low">{t.suggestedTestcases?.priorityLow || '낮음'}</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-bold text-terminal-muted mb-1.5">
                  {t.testcaseManager?.expectedResult || '예상 결과'}
                </label>
                <textarea
                  placeholder={t.testcaseManager?.expectedResultPlaceholder || '예상되는 결과를 입력하세요...'}
                  rows={2}
                  className="w-full px-3 py-2.5 bg-terminal-input border-2 border-black dark:border-terminal-border text-sm text-terminal-text placeholder:text-terminal-muted focus:outline-none focus:border-[var(--neo-mint)] resize-none"
                  data-testid="testcase-create-expected-input"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setIsCreateModalOpen(false)}
                className="flex-1 py-2.5 border-2 border-terminal-border text-terminal-muted hover:text-terminal-text transition-colors text-sm"
                data-testid="testcase-create-cancel-btn"
              >
                {t.testcaseManager?.cancel || '취소'}
              </button>
              <button
                className="flex-1 py-2.5 neo-btn bg-[var(--neo-mint)] text-black text-sm"
                data-testid="testcase-create-save-btn"
              >
                {t.testcaseManager?.save || '저장'}
              </button>
            </div>
            
            {/* 목업 안내 */}
            <p className="text-center text-xs text-terminal-muted mt-4">
              ⚠️ {lang === 'ko' ? '현재 미리보기 모드입니다. 저장 기능은 곧 출시됩니다.' : 'Preview mode. Save functionality coming soon.'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

